import { useCallback, useRef } from 'react';

import {
  S3MultipartFile,
  useS3MultipartUpload
} from '../../../../../../../s3Multipart/hooks/useS3MultipartUpload';

interface UseScreenshotUploadResult {
  uploadScreenshotAsPromise: (file: File) => Promise<string>;
}

function useScreenshotUpload(): UseScreenshotUploadResult {
  // Promise bridge — safe because handleGenerate guards against concurrent calls via isGenerating
  const resolveRef = useRef<((url: string) => void) | null>(null);
  const rejectRef = useRef<((e: Error) => void) | null>(null);

  const handleScreenshotUploaded = useCallback(
    (
      _id: string,
      _uploadedId: string,
      _updatedFile: string,
      file: S3MultipartFile
    ) => {
      const s3Url = file?.presignedUrl?.split('?')[0];
      if (s3Url) {
        resolveRef.current?.(s3Url);
      } else {
        rejectRef.current?.(new Error('No presignedUrl on screenshot upload'));
      }
      resolveRef.current = null;
      rejectRef.current = null;
    },
    []
  );

  const handleScreenshotUploadFailed = useCallback(() => {
    rejectRef.current?.(new Error('Screenshot upload failed'));
    resolveRef.current = null;
    rejectRef.current = null;
  }, []);

  const { onUpload: uploadScreenshot } = useS3MultipartUpload({
    type: 'imageCrops',
    onFileUploaded: handleScreenshotUploaded,
    onFileFailed: handleScreenshotUploadFailed
  });

  const uploadScreenshotAsPromise = useCallback(
    (file: File): Promise<string> =>
      new Promise((resolve, reject) => {
        resolveRef.current = resolve;
        rejectRef.current = reject;
        uploadScreenshot([file]);
      }),
    [uploadScreenshot]
  );

  return { uploadScreenshotAsPromise };
}

export default useScreenshotUpload;
